import {
  formTypeOptions,
  TFormRowData,
} from "../../views/common/components/types";
import { TFixedNumberProcessResult } from "./types";
import formStore from "../../store/formStore";
import {
  WORKFLOW_STATUS,
  WORKFLOW_STATUS_IN_CUSTOMER,
  WORKFLOW_STATUS_IN_DEALER,
} from "../../constants/workflowStatus";
import { TERMINAL_TYPE_LIST } from "../../constants/terminal";

/**
 * steraターミナルの回線種別選択肢
 */
const STATIONARY_OPTIONS: formTypeOptions[] = [
  { "1": "有線LAN" },
  { "2": "無線LAN" },
];
/**
 * steraモバイルの回線種別選択肢
 */
const MOBILE_OPTIONS: formTypeOptions[] = [
  { "3": "LTE" },
  { "4": "Wi-Fi（★事業の合意必須）" },
];

/**
 * 回線種別の選択肢取得
 * @param isStationary
 * @param isMobile
 */
const getCircuitOpt = (isStationary: boolean, isMobile: boolean) => {
  if (isStationary) return STATIONARY_OPTIONS;
  if (isMobile) return MOBILE_OPTIONS;
  return [];
};

/**
 * 端末タイプの選択肢を制御
 * デフォルトで「02: stera mobile（ハンディ型）」を非表示にする
 * @param data: TFormRowData 端末情報フォーム
 * @returns TFixedNumberProcessResult
 */
export const setTerminalTypeOptions: (
  data: TFormRowData
) => TFixedNumberProcessResult = (data: TFormRowData) => {
  const terminalType = data.terminalType[0];
  if (!terminalType) return [];

  // 元の選択肢を取得
  const originalOptions = terminalType.json.formTypeOptions || [];

  const HANDY_JSON = JSON.stringify({ "02": "stera mobile（ハンディ型）" });
  const currentWorkflowStatus = formStore.getWorkflowStatus();
  const isJtbExaminationPhase =
    WORKFLOW_STATUS_IN_DEALER.includes(currentWorkflowStatus) &&
    currentWorkflowStatus >= WORKFLOW_STATUS.EXAMINATION_BY_DEALER; // 50〜110: JTB審査中
  const isHandySelected = terminalType.content === HANDY_JSON;

  // JTB審査中のみ、02が選択済みであれば可視化のため選択肢に含める。
  // それ以外は02を除外（新規で選べないようにする）。
  let filteredOptions = [] as formTypeOptions[];
  if (isJtbExaminationPhase && isHandySelected) {
    filteredOptions = [...originalOptions];
    const hasHandy = filteredOptions.some(
      (option) => JSON.stringify(option) === HANDY_JSON
    );
    if (!hasHandy) {
      filteredOptions.push({
        "02": "stera mobile（ハンディ型）",
      } as formTypeOptions);
    }
  } else {
    filteredOptions = originalOptions.filter(
      (option) => JSON.stringify(option) !== HANDY_JSON
    );
  }

  // 変更が必要かチェック
  const needsUpdate =
    // ハンディ型オプションが存在する場合（除外が必要）
    originalOptions.length !== filteredOptions.length ||
    // disabled が true の場合（false に変更が必要）
    terminalType.disabled === true;

  // 変更が必要な場合のみ更新を返す
  if (needsUpdate) {
    return [
      {
        column: "terminalType",
        content: terminalType.content,
        formTypeOptions: filteredOptions,
        disabled: false,
      },
    ];
  }

  // 変更不要の場合は空配列を返す（無限ループ回避）
  return [];
};

/**
 * 端末タイプによる制御
 * @param data: TFormRowData 端末情報フォーム
 * @param terminalGridId: 端末グリッドID
 * @returns TFixedNumberProcessResult
 */

export const autoCalcByTerminalType: (
  data: TFormRowData,
  terminalGridId?: number
) => TFixedNumberProcessResult = (data: TFormRowData, terminalGridId = 0) => {
  // 端末タイプ取得
  const terminalType = data.terminalType[0];
  if (!terminalType) return [];

  const isStationary =
    terminalType.content === JSON.stringify({ "01": "steraターミナル" }) ||
    terminalType.content ===
      JSON.stringify({ "01": "stera terminal（据置型）" });
  const UTP11_STR =
    TERMINAL_TYPE_LIST.get("UT-P11（ハンディ型）") ||
    JSON.stringify({ "05": "UT-P11（ハンディ型）" });
  const isMobile = terminalType.content === UTP11_STR;
  const HANDY_STR = JSON.stringify({ "02": "stera mobile（ハンディ型）" });
  const isHandy = terminalType.content === HANDY_STR;

  const ret: TFixedNumberProcessResult = [];

  // 回線種別の値を取得
  const circuitType = data.circuitType[0];
  const LTE_STR = JSON.stringify({ "3": "LTE" });
  const WIFI_STR = JSON.stringify({ "4": "Wi-Fi（★事業の合意必須）" });

  // 回線種別のデフォルト値と制御
  let circuitTypeContent = circuitType.content;
  let circuitTypeDisabled = false;

  // 端末の初期状態を取得
  const terminalState = formStore.getTerminalInitialState(terminalGridId);

  // 初期状態が設定されていない場合は現在の回線種別を保存
  if (terminalState.initialCircuitType === null && circuitType.content) {
    formStore.setTerminalInitialState(terminalGridId, circuitType.content);
  }

  // UT-P11（ハンディ型）から他の端末タイプに変更される時、回線種別がWi-Fiなら保持
  if (!isMobile && circuitType.content === WIFI_STR) {
    formStore.setTerminalInitialState(terminalGridId, WIFI_STR);
  }

  // --- ワークフローステータスによる判定 --- //
  const currentWorkflowStatus = formStore.getWorkflowStatus();

  // 既存の定数を使用した判定
  const isInitialPhase =
    currentWorkflowStatus === WORKFLOW_STATUS.INPUT_DEAL_BY_DEALER; // 10: 初期（JTB側）
  const isGuestInputPhase = WORKFLOW_STATUS_IN_CUSTOMER.includes(
    currentWorkflowStatus
  ); // 20,30,40: 加盟店側入力
  const isJtbExaminationPhase =
    WORKFLOW_STATUS_IN_DEALER.includes(currentWorkflowStatus) &&
    currentWorkflowStatus >= WORKFLOW_STATUS.EXAMINATION_BY_DEALER; // 50〜110: JTB審査中

  // JTB審査中以降の場合、既存端末の編集可否を端末タイプ別に制御
  // ・UT-P11（ハンディ型） の既存端末: 端末タイプおよび回線種別は変更不可
  // ・据置型の既存端末: 端末タイプは変更不可、回線種別は変更可
  if (isJtbExaminationPhase) {
    // 新規端末かどうかを判定（terminalIdsに該当するIDが存在しない場合は新規）
    const terminalIds = formStore.getTerminalIds();
    const isNewTerminal =
      !terminalIds[terminalGridId] || !terminalIds[terminalGridId].id;
    if (!isNewTerminal) {
      // 既存端末の場合のみ制御
      const terminalTypeRow = formStore.getFormRowAllGrid(
        "terminalType",
        terminalGridId
      );
      if (terminalTypeRow && terminalTypeRow[0]) {
        // 既存端末は端末タイプを編集不可
        terminalTypeRow[0].disabled = true;
        if (isMobile) {
          // UT-P11（ハンディ型） の既存端末は回線種別も編集不可
          circuitTypeDisabled = true;
        } else {
          // 据置型の既存端末は回線種別は編集可
          circuitTypeDisabled = false;
        }
      }
    }
  }

  /*
   * 回線種別の初期値設定ルール（ワークフローステータス別）
   *
   * ■ 初期（JTB側）- WorkflowStatus: 10
   * ┌─────────────┬─────────────────┬─────────────┐
   * │   端末タイプ  │     初期値       │   編集可否   │
   * ├─────────────┼─────────────────┼─────────────┤
   * │ UT-P11      │ LTE             │ 可          │
   * │ 据置型       │ 有線LAN、無線LAN  │ 可          │
   * └─────────────┴─────────────────┴─────────────┘
   *
   * ■ 加盟店側入力 - WorkflowStatus: 20,30,40
   * ┌─────────────┬─────────────────┬─────────────┐
   * │   端末タイプ  │     初期値       │   編集可否   │
   * ├─────────────┼─────────────────┼─────────────┤
   * │ UT-P11      │ 状況により        │ 不可        │
   * │             │ ※詳細下記        │             │
   * │ 据置型       │ 有線LAN、無線LAN  │ 可          │
   * └─────────────┴─────────────────┴─────────────┘
   *
   * ※加盟店側でUT-P11（ハンディ型）選択時の詳細ルール:
   * ┌─────────────────┬─────────────────┐
   * │   現在の状態      │   設定される値    │
   * ├─────────────────┼─────────────────┤
   * │ 未選択           │ LTE固定          │
   * │ LTE選択済み      │ LTE固定          │
   * │ Wi-Fi選択済み    │ Wi-Fi固定        │
   * │ 申込端末追加時    │ LTE固定          │
   * └─────────────────┴─────────────────┘
   *
   * ■ JTB審査中 - WorkflowStatus: 50〜110
   * ┌─────────────┬─────────────┬─────────────────┬─────────────┐
   * │   端末タイプ  │   端末状態   │     初期値       │   編集可否   │
   * ├─────────────┼─────────────┼─────────────────┼─────────────┤
   * │ UT-P11      │ 新規追加     │ LTE             │ 可          │
   * │             │ 既存端末     │ 現在値維持        │ 不可        │
   * │ 据置型       │ 新規追加     │ 有線LAN、無線LAN  │ 可          │
   * │             │ 既存端末     │ 有線LAN、無線LAN  │ 可          │
   * └─────────────┴─────────────┴─────────────────┴─────────────┘
   */

  if (isMobile) {
    if (isGuestInputPhase) {
      // 加盟店側入力フェーズ（WorkflowStatus: 20,30,40）：状況に応じた値設定＆編集不可
      if (terminalState.initialCircuitType === WIFI_STR) {
        circuitTypeContent = WIFI_STR;
      } else if (!circuitType.content) {
        circuitTypeContent = LTE_STR;
      } else if (circuitType.content === LTE_STR) {
        circuitTypeContent = LTE_STR;
      } else if (circuitType.content === WIFI_STR) {
        circuitTypeContent = WIFI_STR;
      } else {
        // 想定外の値が入っていた場合も LTE に矯正
        circuitTypeContent = LTE_STR;
      }

      circuitTypeDisabled = true; // 編集不可
    } else if (isInitialPhase || isJtbExaminationPhase) {
      // 初期フェーズ（WorkflowStatus: 10）またはJTB審査中（WorkflowStatus: 50〜110）：LTE初期値、編集可
      circuitTypeContent = JSON.stringify({ "3": "LTE" });
    }
  } else {
    // 据置型：全フェーズ共通で空文字、編集可
    circuitTypeContent = "";
  }

  // 回線種別の選択肢
  let circuitTypeOptions = getCircuitOpt(isStationary, isMobile);
  // 02(ハンディ) の場合: JTB審査中は常に LTE 固定で表示（今回限定の暫定対応）
  if (isHandy && isJtbExaminationPhase) {
    circuitTypeContent = LTE_STR;
    circuitTypeOptions = [{ "3": "LTE" }];
    circuitTypeDisabled = true;
  }

  ret.push({
    column: circuitType.column,
    content: circuitTypeContent,
    formTypeOptions: circuitTypeOptions,
    disabled: circuitTypeDisabled,
  });

  // オプション品（スタイラスペン、POSケーブル）項目設定
  ret.push({
    column: "useStylusPen",
    content: isMobile ? JSON.stringify({ "0": "不要" }) : "",
    disabled: isMobile,
  });
  ret.push({
    column: "POSCable",
    content: isMobile ? JSON.stringify({ "0": "不要" }) : "",
    disabled: isMobile,
  });

  // 設置情報（設置方式、セルフ設置端末送付先）項目設定
  ret.push({
    column: "installationMethod",
    content: isMobile ? JSON.stringify({ "2": "セルフ" }) : "",
    disabled: isMobile,
  });
  ret.push({
    column: "selfInstallationTerminalDestinationAddress",
    content: isMobile ? JSON.stringify({ "1": "加盟店店舗" }) : "",
    disabled: isMobile,
  });

  // DHCP 設定（端末タイプが UT-P11（ハンディ型） の場合は「無し」で固定）
  ret.push({
    column: "DHCP",
    content: isMobile ? JSON.stringify({ "2": "無し" }) : "",
    disabled: isMobile,
  });

  return ret;
};

export default autoCalcByTerminalType;
